import React, { useRef, useLayoutEffect } from 'react';
import { gsap, Linear } from 'gsap';
import ScrollMagic from 'scrollmagic';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { useIsMounted } from '@hooks/useIsMounted';
import { Columns } from '@components/Columns';
import {
  StyledSection,
  LeftSectionTextPairing,
  AppTopWave,
  StyledAppBody,
  BlueThread1,
  BlueThread2,
  StyledLogoWrapper,
  StyledLogo,
  LogosWrapper,
  LogosAnimation,
  LogoBox,
} from './LinkedRegistrySection.styles';
import { registryPageData } from '@data-ts/pages/registry';
import { SpacingStack } from '@components/SpacingStack';
import { chunkArray } from '@utils/chunkArray';
import { Flex } from '@components/Flex';

const linkedRegistrySectionData = registryPageData.linkedRegistrySection;

interface LinkedRegistrySectionProps {}

const linkedRegistryLogos = [
  {
    src: 'registry-v2/linked-registries/logo-amazon-v2.svg',
    alt: 'Amazon wedding registry',
  },
  {
    src: 'registry-v2/linked-registries/logo-crate-barrel-v2.svg',
    alt: 'Crate & Barrel wedding registry',
  },
  {
    src: 'registry-v2/linked-registries/logo-rei-v2.svg',
    alt: 'REI wedding registry',
  },
  {
    src: 'registry-v2/linked-registries/logo-target-v2.svg',
    alt: 'Target wedding registry',
  },
  {
    src: 'registry-v2/linked-registries/logo-wayfair-v2.svg',
    alt: 'Wayfair wedding registry',
  },
  {
    src: 'registry-v2/linked-registries/logo-west-elm-v2.svg',
    alt: 'West Elm wedding registry',
  },
  {
    src: 'registry-v2/linked-registries/logo-williams-sonoma-v2.svg',
    alt: 'Williams Sonoma wedding registry',
  },
];

export const LinkedRegistrySection: React.FC<LinkedRegistrySectionProps> = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const tagsRef = useRef<HTMLDivElement>(null);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();

  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let heroTimeline: ReturnType<typeof gsap.timeline>;

    if (scrollController && isMounted) {
      const initTimeline = () => {
        if (scrollController && tagsRef.current) {
          heroTimeline = gsap.timeline({ paused: true, defaults: { ease: Linear.easeNone } });
          heroTimeline.fromTo(
            tagsRef.current,
            {
              transform: 'translateX(5vw)',
            },
            {
              transform: 'translateX(-100vh)',
            },
            0
          );
        }
      };
      const initScene = () => {
        scene = new ScrollMagic.Scene({
          triggerElement: '#logos-wrapper',
          triggerHook: 'onEnter',
          duration: '200%',
        })
          .addTo(scrollController)
          // workaround from here https://github.com/janpaepke/ScrollMagic/issues/918 until plugin for gsap 3 released https://github.com/janpaepke/ScrollMagic/pull/920 or we update it locally
          .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
            heroTimeline?.progress(event.progress);
          });
      };
      initTimeline();
      initScene();
    }
    const clearScene = () => {
      if (heroTimeline) {
        heroTimeline.progress(0);
        heroTimeline.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return () => {
      clearScene();
    };
  }, [scrollController, isMounted]);

  const length = linkedRegistryLogos.length;

  const inputArr = linkedRegistryLogos.map((item, index) => (
    <LogoBox key={index} marginX={[6, 10, 10, 10, 12]}>
      <StyledLogo alt={item.alt} src={item.src} />
    </LogoBox>
  ));

  const columns = chunkArray(inputArr, length).map((rowContent, index) => {
    return <StyledLogoWrapper key={index}>{rowContent}</StyledLogoWrapper>;
  });

  return (
    <StyledSection as="section" ref={sectionRef} id="Linked_Registries" marginTop={['-6rem']}>
      <AppTopWave src="registry-v2/linked-registries/linked-registries-top-wave.svg" />
      <StyledAppBody backgroundColor="accentBlue5">
        <Columns centered>
          <Columns.Column width={5}>
            <LeftSectionTextPairing
              alignment="center"
              textAlign="center"
              eyebrow={linkedRegistrySectionData.eyebrow}
              eyebrowColor="humana3"
              eyebrowVariant="sectionEyeBrow20"
              eyebrowTagName="h3"
              title={linkedRegistrySectionData.title}
              titleVariant="hed56"
              titleColor="humana1"
              titleTagName="h2"
              body={linkedRegistrySectionData.body}
              bodyVariant="dek20"
              bodyColor="neutra1"
              bodyTagName="p"
            />
          </Columns.Column>
        </Columns>
        <BlueThread1 src="registry-v2/linked-registries/blue-thread-1.svg" />
        <BlueThread2 src="registry-v2/linked-registries/blue-thread-2.svg" />
      </StyledAppBody>
      <Flex marginTop={[-2]} backgroundColor="accentBlue5">
        <LogosWrapper id="logos-wrapper" ref={tagsRef}>
          <LogosAnimation alignItems="center">
            <SpacingStack>{columns}</SpacingStack>
          </LogosAnimation>
        </LogosWrapper>
      </Flex>
    </StyledSection>
  );
};
